import {useState} from 'react'
import {DataCDR, PromptTemplateSpec, PromptTemplateSpecField} from '../../core/_models'

const parseLlmRecord = (value: unknown): Record<string, any> => {
  if (!value) return {}

  if (typeof value === 'string') {
    try {
      const parsedValue = JSON.parse(value)
      return parseLlmRecord(parsedValue)
    } catch {
      return {}
    }
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, any>
  }

  return {}
}

export function LlmInnerTab({data}: {data: DataCDR}) {
  const [isPromptVisible, setIsPromptVisible] = useState<boolean>(false)

  const llmAnalysis = data.llm_analysis as Record<string, any> | undefined
  const parsedLlmResult = parseLlmRecord(
    llmAnalysis?.result ?? (llmAnalysis && !('result' in llmAnalysis) ? llmAnalysis : undefined)
  )
  const llmResult = Object.keys(parsedLlmResult).length > 0 ? parsedLlmResult : {}
  const promptText = data.llm_analysis?.prompt_templates?.text || ''
  const promptSpec: PromptTemplateSpec | undefined = data.llm_analysis?.prompt_templates?.spec

  type FieldType = NonNullable<PromptTemplateSpecField['type']>

  const splitFieldsByType = (fields: PromptTemplateSpecField[] = []): Record<FieldType, PromptTemplateSpecField[]> => {
    const grouped: Record<FieldType, PromptTemplateSpecField[]> = {
      string: [],
      number: [],
      boolean: [],
      text: [],
    }

    for (const field of fields) {
      if (!field.type) continue

      grouped[field.type].push(field)
    }

    return grouped
  }

  const sortedFields = splitFieldsByType(promptSpec?.fields)

  const getLlmValue = (key: string) => {
    const value = llmResult[key]

    if (value === null || value === undefined || value === '') return '—'
    if (typeof value === 'object') return JSON.stringify(value)

    return String(value)
  }

  return (
    <div className='h-100 overflow-auto pe-2 pt-3' style={{minHeight: 0, maxHeight: '100%'}}>
      <div className='rounded border bg-body p-4 mb-4'>
        <div className='d-flex align-items-center justify-content-between gap-3'>
          <div>
            <span className='badge badge-light-info small mb-1 me-2'>Промт</span>
            <div className='fw-semibold'>{promptSpec?.template_name}</div>
            <div className='text-muted fs-8'>{promptSpec?.template_description}</div>
          </div>
          <button
            className='btn btn-sm btn-light-primary'
            type='button'
            onClick={() => setIsPromptVisible((value) => !value)}
          >
            {isPromptVisible ? 'Скрыть' : 'Показать'}
          </button>
        </div>

        {isPromptVisible && (
          <div className='mt-4'>
            <pre className='rounded bg-light p-4 mb-3 text-gray-800' style={{whiteSpace: 'pre-wrap'}}>
              {promptText || 'Исходный промт отсутствует'}
            </pre>
          </div>
        )}
      </div>
      {Object.keys(llmResult).length === 0 ? (
        <div className='rounded border border-dashed p-6 text-center text-muted'>LLM анализ звонка отсутствует</div>
      ) : (
        <>
          {sortedFields.string && (
            <div className='row g-3 mb-3'>
              {sortedFields.string.map((spec, i) => {
                const value = getLlmValue(spec.key!)

                return (
                  <div key={i} className='col-12 col-md-6 col-xl-3'>
                    <div className='rounded border p-3 h-100 bg-body'>
                      <div className='text-muted fs-8 mb-1'>{spec.label}</div>
                      <div className='fw-semibold small'>{value}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {sortedFields.text && (
            <div className='row g-3 mb-3'>
              {sortedFields.text.map((spec, i) => (
                <div key={i} className='col-12 col-xl-6'>
                  <div className='rounded border p-4 h-100 bg-body'>
                    <div className='text-muted fs-8 mb-2'>{spec.label!}</div>
                    <div className='fw-semibold text-gray-800'>{getLlmValue(spec.key!)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {sortedFields.number && (
            <div className='row g-3 mb-3'>
              {sortedFields.number.map((spec, i) => {
                const value = getLlmValue(spec.key!)

                return (
                  <div key={i} className='col-12 col-md-6 col-xl-3'>
                    <div className='rounded border p-3 h-100 bg-body'>
                      <div className='text-muted fs-8 mb-1'>{spec.label!}</div>
                      <div className='badge badge-light-primary fw-semibold'>{value}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {sortedFields.boolean && (
            <div className='rounded border bg-body p-4 mb-3'>
              <div className='text-muted fs-8 mb-3'>Критерии оценки</div>
              <div className='row g-2'>
                {sortedFields.boolean.map((spec, i) => {
                  const value = getLlmValue(spec.key!)

                  return (
                    <div key={i} className='col-12 col-md-6'>
                      <div className='d-flex align-items-start justify-content-between gap-2 rounded px-3 py-2 bg-light'>
                        <span className='text-gray-700'>{spec.label!}</span>
                        <span className='badge badge-light-primary fw-semibold'>
                          {value === 'true' ? 'Да' : value === 'false' ? 'Нет' : value}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
